<script setup>
import { ref, onMounted } from 'vue'

const frequency = ref(145)
const dipoleLength = ref(0)
const quarterWaveLength = ref(0)
const fiveEighthLength = ref(0)
const jPoleLength = ref(0)

// 计算天线长度
function calculateAntennaLengths() {
  const freqMHz = parseFloat(frequency.value)
  if (isNaN(freqMHz) || freqMHz <= 0) {
    frequency.value = 145 // 默认值
    return
  }
  
  // 计算波长（米）
  const wavelength = 300 / freqMHz
  
  // 计算各种天线长度
  dipoleLength.value = (wavelength * 0.5 * 0.95).toFixed(2) // 半波偶极天线（考虑末端效应）
  quarterWaveLength.value = (wavelength * 0.25 * 0.95).toFixed(2) // 1/4波垂直天线
  fiveEighthLength.value = (wavelength * 0.625 * 0.95).toFixed(2) // 5/8波垂直天线
  jPoleLength.value = (wavelength * 0.75 * 0.95).toFixed(2) // J极天线垂直部分
}

// 初始化计算
onMounted(() => {
  calculateAntennaLengths()
})
</script>

<template>
  <div class="antenna-calculator">
    <h2>天线计算器</h2>
    <p>计算不同类型天线的尺寸和参数。</p>
    
    <div class="input-group">
      <label for="frequency-input">频率 (MHz):</label>
      <input 
        type="number" 
        id="frequency-input" 
        v-model.number="frequency" 
        min="0.1" 
        step="0.1"
      >
    </div>
    
    <button @click="calculateAntennaLengths">计算天线尺寸</button>
    
    <div class="results-container">
      <div class="result-item">
        <div class="result-title">半波偶极天线 (λ/2)</div>
        <div class="result-value">{{ dipoleLength }} 米</div>
        <div class="result-desc">总长度，每臂长度为此值的一半</div>
      </div>
      
      <div class="result-item">
        <div class="result-title">1/4波垂直天线</div>
        <div class="result-value">{{ quarterWaveLength }} 米</div>
        <div class="result-desc">垂直辐射器长度，需要接地平面</div>
      </div>
      
      <div class="result-item">
        <div class="result-title">5/8波垂直天线</div>
        <div class="result-value">{{ fiveEighthLength }} 米</div>
        <div class="result-desc">提供较低辐射角，适合移动通信</div>
      </div>
      
      <div class="result-item">
        <div class="result-title">J极天线</div>
        <div class="result-value">{{ jPoleLength }} 米</div>
        <div class="result-desc">垂直部分总长度</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.antenna-calculator {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 15px;
}

h2 {
  color: #333;
  margin-bottom: 10px;
  font-size: 1.5rem;
}

p {
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
}

.calculator-container {
  margin-bottom: 20px;
}

.input-group {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.input-group label {
  margin-right: 10px;
  font-weight: 500;
  min-width: 120px;
}

.input-group input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  flex: 1;
  min-width: 150px;
}

.input-group input:focus {
  outline: none;
  border-color: #3a8c3a;
  box-shadow: 0 0 0 2px rgba(58, 140, 58, 0.2);
}

.results-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.result-item {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 15px;
  transition: transform 0.2s ease;
}

.result-item:hover {
  transform: translateY(-2px);
}

.result-title {
  font-weight: 600;
  color: #3a8c3a;
  margin-bottom: 8px;
  font-size: 1.1rem;
}

.result-value {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #333;
}

.result-desc {
  font-size: 0.9rem;
  color: #777;
  line-height: 1.4;
}

button {
  background-color: #3a8c3a;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

button:hover {
  background-color: #2d6a2d;
}

button:disabled {
  background-color: #aaa;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .results-container {
    grid-template-columns: 1fr;
  }
  
  .input-group {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .input-group label {
    margin-bottom: 8px;
  }
  
  .input-group input {
    width: 100%;
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  h2, .result-value {
    color: #e0e0e0;
  }
  
  p {
    color: #bbb;
  }
  
  .result-item {
    background-color: #1e1e1e;
    border-color: #444;
  }
  
  .result-title {
    color: #7cb342;
  }
  
  .result-desc {
    color: #aaa;
  }
  
  .input-group input {
    background-color: #2c2c2c;
    border-color: #444;
    color: #e0e0e0;
  }
  
  .input-group input:focus {
    border-color: #7cb342;
    box-shadow: 0 0 0 2px rgba(124, 179, 66, 0.2);
  }
  
  button:hover {
    background-color: #558b2f;
  }
}
</style>



